import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, concatMap, mergeMap, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ManageCampaignActions } from './manage-campaign.actions';
import { ManageCampaignService } from '../../service/manage-campaign.service';
import { CampaignDashboardService } from '../../service/campaign-dashboard.service';
import { CampaignDashboardActions } from '../campaign-dashboard/campaign-dashboard.actions';
import { MessageService } from 'primeng/api';


@Injectable()
export class ManageCampaignEffects {

  private readonly messageService = inject(MessageService);

  loadCampaigns$ = createEffect(
    (svc: ManageCampaignService = inject(ManageCampaignService)) => {
      return this.actions$.pipe(
        ofType(ManageCampaignActions.loadManageCampaigns),
        concatMap(({ campaignId, parameters, fields, dashboardType }) =>
          svc.getLegalEntitiesIcs(campaignId, parameters, dashboardType).pipe(
            map((manageCampaigns) =>
              ManageCampaignActions.loadManageCampaignsSuccess({ manageCampaigns, fields })
            ),
            catchError((error) =>
              of(ManageCampaignActions.loadManageCampaignsFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  loadAllCampaigns$ = createEffect(
    (svc: ManageCampaignService = inject(ManageCampaignService)) => {
      return this.actions$.pipe(
        ofType(ManageCampaignActions.loadAllManageCampaigns),
        concatMap(({ campaignId, parameters, fields, dashboardType }) =>
          svc.getLegalEntitiesIcs(campaignId, parameters, dashboardType).pipe(
            map((manageCampaigns) =>
              ManageCampaignActions.loadAllManageCampaignsSuccess({ manageCampaigns, fields })
            ),
            catchError((error) =>
              of(ManageCampaignActions.loadAllManageCampaignsFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  closeCampaign$ = createEffect(
    (cd: CampaignDashboardService = inject(CampaignDashboardService)) => {
      return this.actions$.pipe(
        ofType(ManageCampaignActions.closeCampaign),
        concatMap(({ campaignId, dashboardType }) => {
          const close$ = dashboardType === 'ATTESTATION'
            ? cd.closeAttestationCampaign(campaignId)
            : cd.closeEvaluationCampaign(campaignId);

          return close$.pipe(
            mergeMap((campaign) => {
              this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Campaign closed' });
              // Senza il reload della lista lo status in store resterebbe IN_PROGRESS:
              // badge e bottoni della dashboard non si aggiornerebbero fino al refresh.
              return [
                ManageCampaignActions.closeCampaignSuccess({ campaign: { ...campaign, id: campaignId }, dashboardType }),
                dashboardType === 'ATTESTATION'
                  ? CampaignDashboardActions.loadAllAttestationCampaigns()
                  : CampaignDashboardActions.loadAllEvaluationCampaigns(),
                dashboardType === 'ATTESTATION'
                  ? CampaignDashboardActions.loadAttestationCampaignInProgress()
                  : CampaignDashboardActions.loadEvaluationCampaignInProgress()
              ];
            }),
            catchError((error) => {
              this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to close campaign' });
              return of(ManageCampaignActions.closeCampaignFailure({ error }));
            })
          );
        })
      );
    },
    { functional: true }
  );

  updateCampaign$ = createEffect(
    (svc: ManageCampaignService = inject(ManageCampaignService)) => {
      return this.actions$.pipe(
        ofType(ManageCampaignActions.updateCampaign),
        switchMap(({ campaignId, legalEntitiesIds, dashboardType, parameters }) =>
          svc.updateScope(campaignId, legalEntitiesIds, dashboardType).pipe(
            mergeMap((updatedEntities) => [
              ManageCampaignActions.updateCampaignSuccess({ updatedEntities }),
              CampaignDashboardActions.loadCampaignDashboards({ campaignId, dashboardType, parameters }),
              CampaignDashboardActions.loadAllCampaignDashboards({ campaignId, dashboardType })
            ]),
            catchError((error) =>
              of(ManageCampaignActions.updateCampaignFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  constructor(private actions$: Actions) { }
}
